/**
 * Shared badge for a phantom door (`docs/PHANTOM.md` §5): a written
 * confidence badge — "Non inscrit ? · confiance forte / moyenne / faible" —
 * with the inference's hypothesis sentence underneath. Renders nothing for
 * a real door. Used by the guidance and building screens, and reused as-is
 * by the household feature's phantom cards.
 */
import { Badge } from '../../../components/ui';
import { t } from '../../../i18n/fr';
import { phantomConfidenceLabel } from '../utils/phantom';

export interface PhantomBadgeProps {
  /**
   * The door's origin — nothing is rendered when this is `'real'` or
   * `undefined` (the schema's `Door.kind` and `BuildingDoor.kind` are both
   * optional at the type level, even though the API always sends one).
   */
  kind: string | undefined;
  /** H1/H2 inference score in [0, 1], or `null`/`undefined` when unscored. */
  confidence: number | null | undefined;
  /** The inference's hypothesis sentence, or `null`. */
  hypothesis: string | null;
}

/**
 * Render a phantom door's confidence badge and hypothesis sentence.
 *
 * @param props - The door's origin, confidence score and hypothesis.
 * @returns The badge element, or `null` for a real (or unknown-kind) door.
 */
export function PhantomBadge({ kind, confidence, hypothesis }: PhantomBadgeProps) {
  if (!kind || kind === 'real') return null;

  const confidenceLabel = phantomConfidenceLabel(confidence ?? null);
  const badgeText = confidenceLabel
    ? t('session.phantom.badge', { confidence: confidenceLabel })
    : t('session.phantom.badgeUnknown');

  return (
    <div className="phantom-badge">
      <Badge tone="yellow">{badgeText}</Badge>
      {hypothesis ? (
        <p className="muted" style={{ marginTop: 'var(--space-1)' }}>
          {hypothesis}
        </p>
      ) : null}
    </div>
  );
}
